/**
 * The Edit sheet's arithmetic, kept apart from the sheet so every case is
 * pinned by a test rather than by a render: what "Reads" offers and what each
 * choice attaches, which repositories a Save attaches and detaches, and the
 * answerers map a list of people-with-a-kind becomes.
 */

import type { DeskAttachment, DeskAttachmentInput, TrackerOptionsResponse } from "@yagni/shared";

import {
  BACKLOG_SOURCE_WORD,
  QUESTION_KINDS,
  backlogKindWord,
  truncatedQueryWords,
} from "./copy.js";
import type { TeamAnswerers } from "./teamsApi.js";

/** "Reads" left empty: no tracker, and Bailey proposes from the code. */
export const READS_NONE = "none";

export interface ReadsChoice {
  value: string;
  label: string;
  /** What choosing it attaches. Null for "no tracker". */
  attachment: DeskAttachmentInput | null;
  /** The full text the label shortened, for a hover (`title`). */
  title?: string;
}

const TRACKER_ATTACHMENT_KINDS = new Set(["tracker_project", "tracker_epic", "tracker_view"]);

/** The Team's tracker attachments, in the order they were attached. */
export function trackerAttachments(attachments: readonly DeskAttachment[]): DeskAttachment[] {
  return attachments.filter((entry) => TRACKER_ATTACHMENT_KINDS.has(entry.kind));
}

function sourceWord(source: string): string {
  return BACKLOG_SOURCE_WORD[source] ?? source;
}

/**
 * Everything "Reads" can offer, in one list: the backlog the Team reads now
 * (so a Team attached from a pasted link keeps its answer), the workspace's
 * Linear teams and projects and Jira projects, then no tracker.
 */
export function readsChoices(
  options: TrackerOptionsResponse | null,
  attachments: readonly DeskAttachment[],
): ReadsChoice[] {
  const out: ReadsChoice[] = [];
  const seen = new Set<string>();
  const push = (choice: ReadsChoice) => {
    const key = choice.attachment
      ? `${choice.attachment.source}:${choice.attachment.ref}`
      : choice.value;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(choice);
  };

  for (const entry of trackerAttachments(attachments)) {
    const kind = backlogKindWord(entry.kind, entry.source, entry.ref);
    // A pasted JQL's own label is the raw query (or a legacy "JQL: …"
    // prefix on it), unbounded; a surface prints it whole-token cut (YAG-829),
    // with the full query for the hover.
    const query = entry.ref.slice("jql:".length).trim();
    const label = entry.label?.trim() ?? "";
    const isJql =
      entry.source === "jira" &&
      entry.ref.startsWith("jql:") &&
      (!label || label === query || label.toLowerCase().startsWith("jql:"));
    const title = isJql ? query : undefined;
    const shown = isJql ? truncatedQueryWords(query) : (entry.label ?? entry.ref);
    push({
      value: `attached:${entry.id}`,
      label: `${sourceWord(entry.source)} · ${shown}${kind ? ` ${kind}` : ""}`,
      ...(title ? { title } : {}),
      attachment: {
        kind: entry.kind,
        source: entry.source,
        ref: entry.ref,
        label: entry.label,
      },
    });
  }

  for (const team of options?.linear.teams ?? []) {
    push({
      value: `linear-team:${team.id}`,
      label: `Linear · ${team.name} team`,
      attachment: {
        kind: "tracker_project",
        source: "linear",
        ref: team.id,
        label: `${team.name} (${team.key})`,
      },
    });
  }
  for (const project of options?.linear.projects ?? []) {
    push({
      value: `linear-project:${project.id}`,
      label: `Linear · ${project.name} project`,
      attachment: {
        kind: "tracker_project",
        source: "linear",
        ref: project.id,
        label: project.name,
      },
    });
  }
  for (const project of options?.jira.projects ?? []) {
    push({
      value: `jira:${project.key}`,
      label: `Jira · ${project.name} (${project.key})`,
      attachment: {
        kind: "tracker_project",
        source: "jira",
        ref: project.key,
        label: `${project.name} (${project.key})`,
      },
    });
  }

  push({ value: READS_NONE, label: "No tracker, propose from the code", attachment: null });
  return out;
}

/** What "Reads" shows for this Team: its first tracker attachment, or none. */
export function currentReads(
  choices: readonly ReadsChoice[],
  attachments: readonly DeskAttachment[],
): string {
  const first = trackerAttachments(attachments)[0];
  if (!first) return READS_NONE;
  return (
    choices.find(
      (choice) => choice.attachment?.source === first.source && choice.attachment.ref === first.ref,
    )?.value ?? READS_NONE
  );
}

/** Which repositories a Save attaches, and which attachment ids it detaches. */
export function repoChanges(
  attachments: readonly DeskAttachment[],
  chosen: readonly string[],
): { attach: string[]; detach: string[] } {
  const current = attachments.filter((entry) => entry.kind === "repo");
  const currentRefs = new Set(current.map((entry) => entry.ref));
  const chosenRefs = new Set(chosen);
  return {
    attach: chosen.filter((ref) => !currentRefs.has(ref)),
    detach: current.filter((entry) => !chosenRefs.has(entry.ref)).map((entry) => entry.id),
  };
}

/** One person the Team asks, and about what. */
export interface AskRow {
  userId: string;
  kind: string;
}

/** The Team's answerers map, read as one row per person per kind. */
export function askRowsFrom(answerers: TeamAnswerers): AskRow[] {
  const rows: AskRow[] = [];
  for (const { key } of QUESTION_KINDS) {
    for (const userId of answerers[key] ?? []) rows.push({ userId, kind: key });
  }
  return rows;
}

/**
 * The rows, written back as the whole answerers setting. Every kind is sent,
 * because the route reads a missing kind as "unchanged" and an empty one as
 * "nobody", and a person removed from the sheet means nobody.
 */
export function answerersFrom(rows: readonly AskRow[]): TeamAnswerers {
  const out: TeamAnswerers = {};
  for (const { key } of QUESTION_KINDS) out[key] = [];
  for (const row of rows) {
    const list = out[row.kind] ?? (out[row.kind] = []);
    if (!list.includes(row.userId)) list.push(row.userId);
  }
  return out;
}

/** The two audiences the sheet offers. Private reads as members only. */
export type SheetAudience = "shared" | "restricted";

export function sheetAudience(visibility: string | null): SheetAudience {
  return visibility === "workspace" || visibility === "shared" ? "shared" : "restricted";
}

/**
 * What a change of what the Team reads writes, in order: the new backlog is
 * attached BEFORE anything is detached, so a refused attach leaves the Team
 * reading what it read before. Only tracker attachments other than the chosen
 * one are detached; a choice that is already attached writes no attach at all.
 */
export function readsWrites(
  attachments: readonly DeskAttachment[],
  chosen: DeskAttachmentInput | null,
): { attach: DeskAttachmentInput | null; detach: string[] } {
  const trackers = trackerAttachments(attachments);
  const same = (entry: DeskAttachment) =>
    chosen != null &&
    entry.kind === chosen.kind &&
    entry.source === chosen.source &&
    entry.ref === chosen.ref;
  return {
    attach: chosen && !trackers.some(same) ? chosen : null,
    detach: trackers.filter((entry) => !same(entry)).map((entry) => entry.id),
  };
}
